import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Box, LoadingSpinner } from 'edenred-ui';

import {
  getHasOrders,
  getIsAuthenticated,
  getLoginState,
  getUserRoles
} from '@epi-selectors/login';
import { generateUrlWithLocale } from '@epi-selectors/navigation';
import { createMenuItems } from '@epi-helpers/Menu/MenuItemsFactoryHelper';
import { isLocationWithMenu } from '@epi-helpers/Menu/MenuLocationHelper';
import { Navbar } from '@epi-core/components/Navbar';
import { useMenuDimensions } from '@epi-shared/hooks';
import storage from '@epi-helpers/StorageHelper';
import { ILayout } from '@epi-models/core/Layout';
import { selectIsOrderInProcess } from '@epi-selectors/benefitGroupSettings';
import { useGetMetadataQuery } from '@epi-api/companyMetadata';
import { useGetDetailsQuery } from '@epi-api/user';
import { useHasVoucherOrdersQuery } from '@epi-api/vouchers';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { getVoucherPageVisibility } from '@epi-helpers/VouchersHelper';

import { Toast } from '../Toast';
import { Sidebar } from '../Sidebar';

const navbarHeight = 73;

const clearLocalStorage = () => storage.clear();

const Layout = ({
  children,
  isAuthenticated,
  roles,
  hasOrders,
  urlWithLocale,
  location
}: ILayout) => {
  const theme = useTheme();
  const { isMobile, isDesktop } = useMenuDimensions();
  const [menuOpen, setMenuOpen] = useState(isDesktop);
  const [overlayVisible, setOverlayVisible] = useState(isMobile && menuOpen);
  const { isUserImpersonate } = useSelector(getLoginState);
  const isOrderInProgress = useSelector(selectIsOrderInProcess);
  const { data: companyMetadata, isLoading: isLoadingMetadata } =
    useGetMetadataQuery(isAuthenticated ? undefined : skipToken, {
      refetchOnMountOrArgChange: false
    });
  const { data: hasVouchers, isLoading: isLoadingVouchers } =
    useHasVoucherOrdersQuery(isAuthenticated ? undefined : skipToken);
  const { data: customerData, isLoading: isLoadingCustomerData } =
    useGetDetailsQuery(isAuthenticated ? undefined : skipToken);

  const showVoucherOrderPage = getVoucherPageVisibility(
    companyMetadata,
    hasVouchers
  );

  const showOnlyVoucherHistory = companyMetadata
    ? !companyMetadata.orderVouchersEnabled && hasVouchers
    : false;

  const menuItems = createMenuItems({
    roles,
    hasOrders,
    urlWithLocale,
    showVoucherOrderPage,
    showOnlyVoucherHistory,
    isPbu: customerData?.isPbu,
    isUserImpersonate
  });
  const locationIncludesMenu = isLocationWithMenu(location);

  const layoutHeight = `calc(100% - ${navbarHeight}px)`;

  useEffect(() => {
    setMenuOpen(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    setOverlayVisible(isMobile && menuOpen);
  }, [menuOpen, isMobile]);

  useEffect(() => {
    if (isUserImpersonate) {
      window.addEventListener('beforeunload', clearLocalStorage);
      return () => {
        window.removeEventListener('beforeunload', clearLocalStorage);
      };
    }
  }, [isUserImpersonate]);

  if (isLoadingVouchers || isLoadingMetadata || isLoadingCustomerData) {
    return <LoadingSpinner />;
  }

  return (
    <Box height="100vh" max-height="100vh" overflow="hidden">
      <Navbar sideMenuOpen={menuOpen} toggleSideMenu={setMenuOpen} />
      <Box display="flex" height={layoutHeight} position="relative">
        {locationIncludesMenu && isAuthenticated && (
          <Sidebar
            isOpen={menuOpen}
            toggleMenu={setMenuOpen}
            menuItems={menuItems}
            isOrderInProgress={isOrderInProgress}
          />
        )}
        <Box
          width="100%"
          overflow="auto"
          bgcolor={theme.gray5}
          position="relative"
        >
          {overlayVisible && (
            <Box
              zIndex={90}
              width="100%"
              height="100%"
              bgcolor="rgba(0,0,0,0.7)"
              position="fixed"
            />
          )}
          <Toast />
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const connectToStore = connect(state => ({
  hasOrders: getHasOrders(state),
  roles: getUserRoles(state),
  isAuthenticated: getIsAuthenticated(state),
  urlWithLocale: url => generateUrlWithLocale(url)(state)
}));

const ConnectedLayout = connectToStore(withRouter(Layout));

export { ConnectedLayout as Layout };
